<template>
  <OperationItemSkeleton v-if="!operation" />
  <div
    v-if="operation"
    class="operations-list__operation"
  >
    <li class="operations-list__operation-inner">
      <div class="operations-list__operation-header">
        <router-link
          class="operations-list__operation-header-title typo-large"
          :to="itemRoutes.view"
        >
          {{ operation.name }}
          <div
            v-if="operation?.process_group?.name"
            class="operations-list__operation-header-status operations-list__operation-header-status--blue hide-for-small show-for-medium show-for-large"
          >
            {{ operation.process_group.name }}
            <div
              v-if="operation?.process_group?.description"
              class="operations-list__pop typo-small-title"
            >
              {{ operation.process_group.description }}
            </div>
          </div>

          <div
            v-if="operation?.type_group?.name"
            class="operations-list__operation-header-status hide-for-small show-for-medium show-for-large"
          >
            {{ operation.type_group.name }}
            <div
              v-if="operation?.type_group?.description"
              class="operations-list__pop typo-small-title"
            >
              {{ operation.type_group.description }}
            </div>
          </div>
        </router-link>
        <Dropdown
          v-if="operation.policy?.edit"
          :items="actionMenuList"
          @click-item="setDialogType"
        >
          <UseSvg
            class="icon"
            name="vertical_ellipsis"
          />
        </Dropdown>
      </div>
      <router-link
        class="operations-list__operation-content"
        :to="itemRoutes.view"
      >
        <div
          :class="{
            'operations-list__operation-link typo-title': true,
            'operations-list__operation-link--space-between': isShaft,
          }"
        >
          <div class="operations-list__last-inspected-group show-for-large">
            <div class="operations-list__last-inspected-label">
              <span class="typo-super-tiny">
                {{ $t('operations.last_inspected') }}
              </span>
            </div>
            <div class="operations-list__last-inspected-inner">
              <div class="operations-list__last-inspected">
                <div class="typo-small-text">
                  {{ formatDate(operation?.last_inspection_date) || '-' }}
                </div>
                <div
                  v-if="operation?.last_inspection_date"
                  class="
                    operations-list__last-inspected-time-ago
                    typo-tiny
                    show-for-large
                  "
                >
                  {{ durationInWords }}
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="barChart"
            class="operations-list__inspection-history show-for-x-large"
          >
            <div class="operations-list__inspection-history-label">
              <span class="typo-super-tiny">
                {{ $t('shafts.inspection_history') }}
              </span>
            </div>
            <div class="operations-list__inspection-history-inner">
              <BarChart
                :id="operation?.name"
                :height="50"
                :series="barChartData(operation?.inspection_history)"
                :width="145"
              />
            </div>
          </div>

          <div
            v-if="isShaft"
            class="operations-list__detail operations-list__detail--padding-left show-for-medium"
          >
            <div class="operations-list__detail-label">
              <span class="typo-super-tiny">
                {{ $t('activerecord.models.shafts/job.other') }}
              </span>
            </div>
            <div class="operations-list__detail-content typo-smallish">
              <div>
                <strong>{{ operation?.jobs?.scheduled_jobs_count }}</strong>
                <span>{{ $t('shafts.scheduled') }}</span>
              </div>
              <div>
                <strong>{{ operation?.jobs?.overdue_jobs_count }}</strong>
                <span>{{ $t('shafts.delayed') }}</span>
              </div>
            </div>
          </div>

          <div
            v-if="isShaft"
            class="operations-list__detail show-for-large"
          >
            <div class="operations-list__detail-strength">
              <div class="operations-list__detail-strength-graph">
                <DonutChart
                  v-if="!seriesHasValues(operation?.members_by_strength_remaining?.series)"
                  :id="operation?.name"
                  :height="70"
                  :labels="operation?.members_by_strength_remaining?.labels"
                  :series="operation?.members_by_strength_remaining?.series"
                  :width="70"
                />
                <DonutChart
                  v-else
                  :id="operation.name"
                  :height="70"
                  :labels="[]"
                  :series="[0, 0, 0, 0, 0, 0, 7]"
                  :width="70"
                />
              </div>
              <div>
                <div class="operations-list__detail-label operations-list__detail-label">
                  <span class="typo-super-tiny">
                    {{ $t('shafts.members_by_strength_remaining') }}
                  </span>
                </div>
                <div class="operations-list__detail-strength-items">
                  <div
                    v-for="(item, itemIndex) in operation?.members_by_strength_remaining?.displayed_conditions"
                    :key="itemIndex"
                    class="operations-list__detail-strength-item"
                  >
                    <div class="operations-list__detail-strength-item-values">
                      <span
                        :class="`operations-list__detail-strength-item-values-indicator operations-list__detail-strength-item-values-indicator--${item.condition_value} typo-smallish`"
                      />
                      {{ item.count }}
                      <span class="operations-list__detail-strength-item-values-percent typo-tiny">
                        ({{ item.percentage }}%)
                      </span>
                    </div>
                    <div class="operations-list__detail-strength-item-values-percent-scale">
                      {{ I18n.t(`condition_percentages.${item.condition_value}`) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="isShaft"
            class="operations-list__detail"
          >
            <div class="operations-list__detail-label">
              <span class="typo-super-tiny">
                {{ $t('shafts.condition_rating') }}
              </span>
            </div>
            <div class="operations-list__detail-content">
              <div :class="`operations-list__detail-condition operations-list__detail-condition--${calculateConditionRating(operation.condition_rating)} typo-small`">
                {{ truncateNumber(operation.condition_rating) }}
              </div>
            </div>
          </div>

          <template v-if="operation.trusted_user && !isShaft">
            <div class="operations-list__condition-4-issues">
              <div class="operations-list__condition-4-issues-group">
                <span class="typo-super-tiny">
                  {{ $t('operations.condition_4_issues') }}
                </span>
                <div class="operations-list__condition-4-issues-group-values">
                  <div class="operations-list__percent show-for-large">
                    {{ percent(operation.condition_4_data.percent) }}
                  </div>
                  <div class="operations-list__value">
                    {{ operation.condition_4_data.count }}
                  </div>
                </div>
              </div>
              <div class="operations-list__graph">
                <div class="graph">
                  <ConditionIndexChart
                    id="condition_4"
                    :series="operation.condition_4_data.graph"
                  />
                </div>
              </div>
            </div>

            <div class="operations-list__condition-5-issues">
              <div class="operations-list__condition-5-issues-group">
                <span class="typo-super-tiny">
                  {{ $t('operations.condition_5_issues') }}
                </span>
                <div class="operations-list__condition-5-issues-group-values">
                  <div class="operations-list__percent">
                    {{ percent(operation.condition_5_data.percent) }}
                  </div>
                  <div class="operations-list__value">
                    {{ operation.condition_5_data.count }}
                  </div>
                </div>
              </div>
              <div
                :class="{
                  'operations-list__graph': true,
                  'show-for-large': true,
                }"
              >
                <div class="graph">
                  <ConditionIndexChart
                    id="condition_5"
                    :series="operation.condition_5_data.graph"
                  />
                </div>
              </div>
            </div>
          </template>
          <div
            v-else-if="!operation.trusted_user && !isShaft"
            class="operations-list__undisclosed typo-small-text"
          >
            {{ $t('operations.undisclosed') }}
          </div>
        </div>

        <div
          v-if="operation?.policy?.show"
          class="operations-list__button"
        >
          <div class="operations-list__detail">
            <div class="button typo-small">
              {{ $t(`operations.view_${operationType}`) }}
            </div>
          </div>
        </div>
      </router-link>
    </li>
  </div>

  <Dialog
    v-if="dialogIsOpen"
    :message="modalCopy.message"
    :title="modalCopy.title"
    @dismiss-dialog="toggleDialog"
  >
    <template
      v-if="modalCopy?.input_placeholder"
      #default
    >
      <div class="top-margin">
        <FormRow
          :label="modalCopy.input_placeholder"
          labelFor="operation_name"
          :modifiers="['no-padding']"
        >
          <div class="form-row__input form-row__input--no-peek">
            <div class="input">
              <input
                id="operation_name"
                ref="operationNameRef"
                v-model="operationName"
                :aria-label="`${modalCopy.type}-input`"
              >
            </div>
          </div>
        </FormRow>
      </div>
    </template>
    <template #actions>
      <button
        :aria-label="`${modalCopy.type}-operation`"
        class="button button--inverse"
        :disabled="!isValid"
        type="button"
        @click="toggleOperation(modalCopy.type)"
      >
        {{ modalCopy.button }}
      </button>
    </template>
  </Dialog>
</template>

<script setup>
import { onMounted, onUnmounted, ref, computed, watch, nextTick } from 'vue';
import I18n from 'i18n-js';

// Utils
import useAjax from '@/functions/use_ajax';
import { formatDate } from '@/functions/date_format_helpers';
import { timeAgoInWords } from '@/functions/distance_of_time_in_words';
import truncateNumber from '@/functions/simm/truncate_number';
import calculateConditionRating from '@/functions/simm/shafts/condition_rating';

// Components
import UseSvg from '@/components/generic/use_svg.vue';
import Dropdown from '@/components/generic/dropdown.vue';
import OperationItemSkeleton from '@/components/operations/operation_item_skeleton.vue';
import BarChart from '@/components/charts/bar_chart.vue';
import DonutChart from '@/components/charts/donut_chart.vue';
import Dialog from '@/components/generic/dialog.vue';
import ConditionIndexChart from '@/components/charts/condition_index_chart.vue';
import FormRow from '@/components/form/row.vue';

const emit = defineEmits(['reload']);

const props = defineProps({
  id: { type: String, required: true, default: null },
  organisationId: { type: String, required: true, default: null },
  endpointIdentifier: { type: String, required: true, default: 'plant' },
  itemRoutes: { type: Object, required: true },
  barChart: { type: Boolean, default: false },
});

const ajax = useAjax();
const operation = ref(null);
const dialogIsOpen = ref(false);
const isValid = ref(true);
const isShaft = computed(() => props.endpointIdentifier === 'shafts');
const isEquipment = computed(() => props.endpointIdentifier === 'equipment');
const isPlant = computed(() => props.endpointIdentifier === 'operations');
const modalCopy = ref([]);
const operationName = ref('');
const operationNameRef = ref(null);

const percent = (n) => `${Math.round(n)}%`;

const operationType = computed(() => {
  switch (props.endpointIdentifier) {
    case 'shafts_v2/shafts':
    case 'shafts':
      return 'shaft';

    case 'ships':
      return 'ship';

    case 'equipment':
      return 'equipment';

    default:
      return 'operation';
  }
});

const durationInWords = computed(() => {
  const duration = timeAgoInWords(operation.value?.last_inspection_date);
  const translation = new Date() > new Date(operation.value?.last_inspection_date) ? 'time_ago' : 'time_from_now';

  return I18n.t(`general.${translation}`, { time_span: duration });
});

const actionMenuList = computed(() => {
  const list = [{ label: I18n.t(`nav.edit_${isShaft.value ? operationType.value : 'configuration'}`), to: props.itemRoutes.edit }];
  operation.value?.policy?.clone && isEquipment.value && list.push({
    label: I18n.t('general.clone'),
    key: 'clone',
    modifiers: ['clone']
  });
  operation.value?.policy?.destroy && !isShaft.value && list.push({
    label: I18n.t('general.delete'),
    key: 'delete',
    modifiers: ['destructive']
  });
  return list;
});

const operationData = computed(() => {
  const baseUrl = `/organisations/${props.organisationId}`;
  const genericURL = `${baseUrl}/${props.endpointIdentifier}`;
  const urlList = {
    operation: `${genericURL}/${props.id}`,
    process: `${genericURL}_processes`,
    type: isPlant.value ? `${baseUrl}/plant_types` : `${genericURL}_types`,
    clone: `${genericURL}/${props.id}/clones`,
    delete: `${genericURL}/${props.id}`,
  };
  return urlList;
});

watch(operationName, (name) => {
  isValid.value = false;
  if (name === operation.value.name) {
    isValid.value = !isValid.value;
  }
});

onMounted(async () => {
  await loadData(operationData.value.operation).then(async (data) => {
    operation.value = data;

    const type = isPlant.value ? 'plant' : operationType.value;
    const processExtension = data[`${type}_process_id`];
    const typeExtension = data[`${type}_type_id`];

    processExtension && (
      operation.value['process_group'] = await loadData(`${operationData.value.process}/${processExtension}`)
    );

    typeExtension && (
      operation.value['type_group'] = await loadData(`${operationData.value.type}/${typeExtension}`)
    );
  });
});

onUnmounted(() => {
  ajax.abort();
});

async function loadData(url, method = 'GET') {
  let { ok, data } = await ajax(url, { method });

  if (ok) {
    return data;
  }
}

function barChartData(operationHistory) {
  return [
    {
      name: I18n.t('nav.inspections'),
      data: operationHistory,
    },
  ];
}

function seriesHasValues(series) {
  return series?.every((item) => item === 0);
}

function setModal(key) {
  const placeHolderType = operationType.value === 'operation' ? 'plant' : operationType.value;
  const text = {
    clone: {
      message: I18n.t('equipment.clone_confirmation_message'),
      title: I18n.t('equipment.clone_confirmation_title'),
      button: I18n.t('general.clone'),
      type: key,
    },
    delete: {
      message: I18n.t('equipment.delete_confirmation_message'),
      title: I18n.t('equipment.delete_confirmation_title'),
      button: I18n.t('general.delete'),
      input_placeholder: I18n.t(`operations.operation_name_label_${placeHolderType}`),
      type: key,
    }
  };

  return text[key];
}

/**
 * Toggles dialog when the clone button is clicked
 */
async function setDialogType({ key }) {
  if (key === 'clone' || key === 'delete') {
    modalCopy.value = setModal(key);
    dialogIsOpen.value = true;
    key === 'delete' && await nextTick(() => {
      isValid.value = false;
      operationNameRef.value.focus();
    });
  }
}

function toggleDialog() {
  dialogIsOpen.value = !dialogIsOpen.value;
}

function toggleOperation(type) {
  type === 'clone' && cloneOperation();
  type === 'delete' && deleteOperation();
}

async function cloneOperation() {
  await loadData(operationData.value.clone, 'POST').then(() => {
    toggleDialog();
    emit('reload');
  });
}

async function deleteOperation() {
  await loadData(operationData.value.delete, 'DELETE').then(() => {
    toggleDialog();
    emit('reload', { type: 'delete', id: props.id });
  });
}
</script>

<style scoped lang="scss">
@import '@/stylesheets/global';

.top-margin {
  margin-top: bu(2);
}
</style>
